import React, { useState } from 'react';

export default function Enquiry() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.phone || !form.message) {
      setError('Please fill in your name, phone number, and enquiry message.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/public/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Submission failed. Please try calling us directly.');
      }
    } catch {
      setError('Could not reach server. Please check your connection or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* PAGE HERO */}
      <section style={{
        paddingTop: 120, paddingBottom: 72,
        background: 'var(--bg1, #0e1f14)',
        textAlign: 'center',
      }}>
        <div className="container">
          <p className="section-label" style={{ color: 'var(--gold)', justifyContent: 'center', display: 'flex' }}>
            Get in Touch
          </p>
          <h1 className="section-title" style={{ fontSize: 'clamp(36px, 5vw, 56px)', textAlign: 'center' }}>
            Place an <em>Enquiry</em>
          </h1>
          <p style={{ color: 'var(--ivory-d)', maxWidth: 520, margin: '20px auto 0', lineHeight: 1.75, fontSize: 15 }}>
            Have a question or want to know more? Send us your details and our team will get back to you within 2 hours during clinic hours.
          </p>
        </div>
      </section>

      {/* ENQUIRY FORM + INFO */}
      <section style={{ background: 'var(--bg, #0a1a0e)', padding: '80px 0 100px' }}>
        <div className="container">
          <div className="enquiry-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)',
            gap: '64px',
            alignItems: 'start',
            maxWidth: 960,
            margin: '0 auto',
          }}>

            {/* Left — clinic info */}
            <div className="fade-up">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                {[
                  { icon: '📍', title: 'Address', text: 'KayaSparsha Ayurvedic Wellness\nKengeri Satellite Town\nBangalore – 560060, Karnataka' },
                  { icon: '🕐', title: 'Clinic Hours', text: 'Monday – Saturday: 8:00 AM – 8:00 PM\nSunday: By Appointment Only' },
                  { icon: '📞', title: 'Call / WhatsApp', text: '+91 96062 09376' },
                  { icon: '✉️', title: 'Email', text: 'support@kayasparsha.com' },
                ].map(item => (
                  <div key={item.title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(181,136,62,0.1)',
                      border: '1px solid rgba(181,136,62,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                    }}>{item.icon}</div>
                    <div>
                      <h4 style={{ margin: '0 0 4px', color: 'var(--ivory)', fontSize: 14, letterSpacing: '1px', textTransform: 'uppercase' }}>
                        {item.title}
                      </h4>
                      <p style={{ margin: 0, color: 'var(--ivory-d)', fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                        {item.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — form */}
            <div className="fade-up enquiry-form-card" style={{
              background: 'var(--bg1, #0e1f14)',
              border: '1px solid rgba(181,136,62,0.18)',
              borderRadius: 16,
              padding: '40px 36px',
            }}>
              {success ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🙏</div>
                  <h3 style={{ color: 'var(--gold)', fontFamily: 'var(--f-serif)', fontSize: 28, margin: '0 0 12px' }}>
                    Thank you!
                  </h3>
                  <p style={{ color: 'var(--ivory-d)', lineHeight: 1.7, marginBottom: 28 }}>
                    Your enquiry has been received. Our team will get back to you within 2 hours during clinic hours.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(181,136,62,0.4)',
                      color: 'var(--gold)',
                      padding: '10px 28px',
                      borderRadius: 4,
                      fontSize: 13,
                      letterSpacing: 1,
                      cursor: 'pointer',
                    }}
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <>
                  <h2 style={{
                    fontFamily: 'var(--f-serif)',
                    fontSize: 26, fontWeight: 400,
                    color: 'var(--ivory)',
                    margin: '0 0 28px',
                    letterSpacing: '0.5px',
                  }}>
                    Send us a message
                  </h2>

                  <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div className="enquiry-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={labelStyle}>Full Name *</span>
                        <input
                          type="text" name="name" value={form.name}
                          onChange={onChange} placeholder="Your name"
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={labelStyle}>Phone *</span>
                        <input
                          type="tel" name="phone" value={form.phone}
                          onChange={onChange} placeholder="+91 96062 09376"
                          style={inputStyle}
                        />
                      </label>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={labelStyle}>Email Address</span>
                      <input
                        type="email" name="email" value={form.email}
                        onChange={onChange} placeholder="your@email.com"
                        style={inputStyle}
                      />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={labelStyle}>Enquiry / Message *</span>
                      <textarea
                        name="message" value={form.message}
                        onChange={onChange} rows={5}
                        placeholder="Describe your health concern, ask about a treatment, or just say hello…"
                        style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                      />
                    </label>

                    {error && (
                      <p style={{ color: '#e57373', fontSize: 13, margin: 0 }}>{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        background: submitting ? 'rgba(181,136,62,0.5)' : 'var(--gold, #c9963a)',
                        color: '#1a3a2a',
                        border: 'none',
                        padding: '14px 32px',
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--f-sans)',
                        transition: 'opacity 0.2s',
                      }}
                    >
                      {submitting ? 'Sending…' : 'Send Enquiry →'}
                    </button>

                    <p style={{ fontSize: 12, color: 'var(--text-light, #7a8a7e)', margin: 0, lineHeight: 1.6 }}>
                      We typically respond within 2 hours during clinic hours (Mon–Sat 8 AM – 8 PM). Your details are never shared.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE MAP placeholder */}
      <section style={{ background: 'var(--bg1, #0e1f14)', padding: '0 0 80px' }}>
        <div className="container" style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{
            height: 260, borderRadius: 12,
            border: '1px solid rgba(181,136,62,0.18)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 16, background: 'rgba(181,136,62,0.04)',
          }}>
            <div style={{ fontSize: 36 }}>📍</div>
            <p style={{ color: 'var(--ivory-d)', fontSize: 14, margin: 0 }}>
              Kengeri Satellite Town, Bangalore – 560060
            </p>
            <a
              href="https://maps.google.com/?q=Kengeri+Satellite+Town+Bangalore"
              target="_blank" rel="noopener noreferrer"
              style={{
                padding: '10px 24px',
                border: '1px solid rgba(181,136,62,0.4)',
                color: 'var(--gold)',
                textDecoration: 'none',
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                borderRadius: 4,
              }}
            >
              Open in Google Maps →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  color: 'var(--ivory-d, #c8d4c0)',
};

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(181,136,62,0.2)',
  borderRadius: 6,
  padding: '11px 14px',
  color: 'var(--ivory, #f0ebe0)',
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s',
  width: '100%',
  boxSizing: 'border-box',
};
